import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import useEstMobile from '../hooks/useEstMobile';
import api from '../services/api';
import { LABELS_ROLES } from '../constants/roles';
import {
  IconeAccueil, IconeParapheur, IconeCloche, IconeCompte, IconeFermer,
} from './IconesSVG';

// Ces roles disposent d'un parapheur ; pour les autres l'onglet renvoie au
// tableau de bord plutot que vers un ecran vide.
const ROLES_PARAPHEUR = ['dg_filiale', 'pdg', 'acheteur', 'preparateur_memo_filiale',
  'preparateur_memo_holding', 'assistante_pdg', 'responsable', 'responsable_service'];

const SECONDAIRE = [
  { libelle: 'Archives', chemin: '/archives' },
  { libelle: 'Plan de classement', chemin: '/classement' },
  { libelle: 'Nouvelle demande', chemin: '/nouveau-document' },
  { libelle: 'Changer mon mot de passe', chemin: '/changer-mot-de-passe' },
];

export default function NavigationMobile() {
  const estMobile = useEstMobile();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, token, logout } = useAuthStore();
  const [nonLues, setNonLues] = useState(0);
  const [menu, setMenu] = useState(false);

  const role = user?.roles?.[0]?.role || user?.role;

  useEffect(() => {
    if (!token || !estMobile) return undefined;
    const charger = async () => {
      try {
        const r = await api.get('/notifications/count');
        setNonLues(r?.total || 0);
      } catch (e) { /* le badge n'est pas critique */ }
    };
    charger();
    const t = setInterval(charger, 30000);
    return () => clearInterval(t);
  }, [token, estMobile, pathname]);

  if (!estMobile || !token) return null;

  const cheminParapheur = ROLES_PARAPHEUR.includes(role)
    ? (role === 'pdg' ? '/parapheur' : '/parapheur-role')
    : '/dashboard';

  const onglets = [
    { cle: 'accueil', libelle: 'Accueil', Icone: IconeAccueil, chemin: '/dashboard' },
    { cle: 'parapheur', libelle: 'Parapheur', Icone: IconeParapheur, chemin: cheminParapheur },
    { cle: 'notifications', libelle: 'Notifications', Icone: IconeCloche, chemin: '/dashboard', badge: nonLues },
    { cle: 'compte', libelle: 'Mon compte', Icone: IconeCompte, action: () => setMenu(true) },
  ];

  const actif = (chemin) => chemin && pathname.startsWith(chemin);

  return (
    <>
      <nav style={s.barre} aria-label="Navigation principale">
        {onglets.map(({ cle, libelle, Icone, chemin, action, badge }) => (
          <button key={cle} type="button" style={{ ...s.onglet, color: actif(chemin) ? '#C0392B' : '#8899AA' }}
            onClick={() => (action ? action() : navigate(chemin))} aria-label={libelle}>
            <span style={s.zoneIcone}>
              <Icone taille={22} />
              {badge > 0 && <span style={s.pastille}>{badge > 9 ? '9+' : badge}</span>}
            </span>
            <span style={s.libelle}>{libelle}</span>
          </button>
        ))}
      </nav>

      {menu && (
        <div style={s.fond} onClick={(e) => { if (e.target === e.currentTarget) setMenu(false); }}>
          <div style={s.panneau}>
            <div style={s.enteteMenu}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#0D1B2A' }}>
                  {user?.prenom} {user?.nom}
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>{LABELS_ROLES[role] || role}</div>
              </div>
              <button type="button" onClick={() => setMenu(false)} style={s.fermer} aria-label="Fermer">
                <IconeFermer taille={22} />
              </button>
            </div>
            {SECONDAIRE.map((e) => (
              <button key={e.chemin} type="button" style={s.entree}
                onClick={() => { setMenu(false); navigate(e.chemin); }}>
                {e.libelle}
              </button>
            ))}
            <button type="button" style={{ ...s.entree, color: '#C0392B', fontWeight: 700 }}
              onClick={() => { setMenu(false); logout(); }}>
              Déconnexion
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const s = {
  barre: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 3500,
    display: 'flex', background: '#fff', borderTop: '0.5px solid #e5e7eb',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
  },
  onglet: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 3, background: 'transparent', border: 'none', cursor: 'pointer',
    minHeight: 60, padding: '6px 2px', fontFamily: 'inherit',
  },
  zoneIcone: { position: 'relative', display: 'flex' },
  pastille: {
    position: 'absolute', top: -5, right: -8, background: '#C0392B', color: '#fff',
    borderRadius: 9, fontSize: 9, fontWeight: 700, padding: '1px 4px', minWidth: 14, textAlign: 'center',
  },
  libelle: { fontSize: 10, fontWeight: 600, letterSpacing: '-0.01em' },
  fond: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 3600, display: 'flex', alignItems: 'flex-end' },
  panneau: { width: '100%', background: '#fff', borderRadius: '16px 16px 0 0', padding: '8px 0 calc(env(safe-area-inset-bottom, 0px) + 12px)' },
  enteteMenu: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '0.5px solid #eee' },
  fermer: { background: 'transparent', border: 'none', color: '#8899AA', cursor: 'pointer', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  entree: { display: 'block', width: '100%', textAlign: 'left', padding: '15px 18px', background: 'transparent', border: 'none', borderBottom: '0.5px solid #f4f4f4', fontSize: 14, color: '#0D1B2A', cursor: 'pointer', fontFamily: 'inherit', minHeight: 48 },
};
